import React from 'react';
import type { MealBreakConfig } from '@/types';
import {
  MealTriggerModeComparison,
} from '@/components/config_forms/shared/help-graphics/MealTriggerModeComparison';
import { TriggerModeDetailsContent } from '../components/TriggerModeDetailsContent';

/* ------------------------------------------------------------------ */
/*  Defaults (from initialState)                                       */
/* ------------------------------------------------------------------ */

const WINDOW_DEFAULTS = {
  deadlineHours: 5,
  gracePeriodMinutes: 0,
} as const;

/* ------------------------------------------------------------------ */
/*  Builder                                                            */
/* ------------------------------------------------------------------ */

interface MealTriggerModeInjectableParams {
  mealBreakConfig: MealBreakConfig;
  onModeChange?: (mode: 'deadline' | 'count') => void;
  // Deadline callbacks
  onDeadlineChange?: (value: number | null) => void;
  onMultipleBreaksChange?: (value: boolean) => void;
  onGracePeriodChange?: (value: number | null) => void;
  // Count callbacks
  onRequiredBreaksChange?: (value: number | null) => void;
  onScalingToggle?: (enabled: boolean) => void;
  onThresholdChange?: (value: number | null) => void;
  onAdditionalBreakIntervalChange?: (value: number | null) => void;
  thresholdFromBilling: number | null;
}

export function buildMealTriggerModeInjectable({
  mealBreakConfig,
  onModeChange,
  onDeadlineChange,
  onMultipleBreaksChange,
  onGracePeriodChange,
  onRequiredBreaksChange,
  onScalingToggle,
  onThresholdChange,
  onAdditionalBreakIntervalChange,
  thresholdFromBilling,
}: MealTriggerModeInjectableParams): {
  injectables: Record<string, React.ReactNode>;
} {
  const activeMode = mealBreakConfig.mealTriggerMode || 'deadline';

  const deadlineHours =
    mealBreakConfig.mealDeadlineHours ?? WINDOW_DEFAULTS.deadlineHours;

  const gracePeriodMinutes =
    mealBreakConfig.gracePeriodMinutes ?? WINDOW_DEFAULTS.gracePeriodMinutes;

  const multipleBreaks =
    mealBreakConfig.recurringMealDeadline ?? false;

  // Count-mode defaults
  const requiredBreaks = mealBreakConfig.requiredBreaks ?? 2;
  const scalingEnabled = mealBreakConfig.additionalBreakIntervalHours != null;
  const thresholdHours = mealBreakConfig.thresholdHours ?? null;
  const additionalBreakIntervalHours = mealBreakConfig.additionalBreakIntervalHours ?? null;

  const comparisonElement = React.createElement(MealTriggerModeComparison, {
    activeMode,
    onModeChange,
  });

  const detailsElement = React.createElement(TriggerModeDetailsContent, {
    activeMode,
    // Deadline
    deadlineHours,
    gracePeriodMinutes,
    multipleBreaks,
    onDeadlineChange,
    onMultipleBreaksChange,
    onGracePeriodChange,
    // Count
    requiredBreaks,
    scalingEnabled,
    thresholdHours,
    additionalBreakIntervalHours,
    thresholdFromBilling,
    onRequiredBreaksChange,
    onScalingToggle,
    onThresholdChange,
    onAdditionalBreakIntervalChange,
  });

  const combinedElement = React.createElement(
    React.Fragment,
    null,
    comparisonElement,
    detailsElement,
  );

  return {
    injectables: {
      meal_trigger_mode: combinedElement,
    },
  };
}
